import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import { adUtils } from 'starry-sky-ui';
import type { VisitStatsData } from '@/service/visit';
import { getVisitStats } from '@/service/visit';
import { shortPage } from './data';
import './index.scss';

// ---- 工具函数 ----

/** 格式化数字（千分位） */
const fmt = (n: number) => n.toLocaleString();

// ---- 组件 ----

const Visualize: React.FC = () => {
    const [stats, setStats] = useState<VisitStatsData | null>(null);
    const [mapReady, setMapReady] = useState(false);

    const mapChartRef = useRef<HTMLDivElement>(null);
    const barChartRef = useRef<HTMLDivElement>(null);
    const pieChartRef = useRef<HTMLDivElement>(null);
    const trendChartRef = useRef<HTMLDivElement>(null);

    // ---- 1. 通过 SSE 获取访客统计数据 ----
    useEffect(() => {
        const controller = new AbortController();

        // 防抖处理：200ms 内多次事件只请求一次
        const debouncedRefresh = adUtils.debounce(async () => {
            try {
                const res = await getVisitStats();
                if (res?.data) {
                    setStats(res.data);
                }
            } catch {
                // 忽略错误
            }
        }, 200);

        const fetchViaSSE = async () => {
            const baseURL =
                process.env.NODE_ENV === 'production'
                    ? `${window.location.origin}/api`
                    : 'http://localhost:3002/api';

            try {
                const response = await fetch(`${baseURL}/visit/stats/stream`, {
                    signal: controller.signal,
                });

                const reader = response.body?.getReader();
                if (!reader) return;

                await adUtils.readSSEStream(
                    reader,
                    (chunk) => {
                        const msg = chunk as unknown as Record<string, unknown>;
                        // 收到 connected 或 page-visit 事件时刷新数据
                        if (
                            msg?.type === 'connected' ||
                            msg?.type === 'page-visit'
                        ) {
                            debouncedRefresh();
                        }
                    },
                    controller.signal,
                );
            } catch {
                // 忽略中止错误
            }
        };

        fetchViaSSE();

        return () => {
            controller.abort();
        };
    }, []);

    // ---- 2. 加载中国地图 ----
    useEffect(() => {
        fetch('/china.json')
            .then((r) => r.json())
            .then((geoJson) => {
                echarts.registerMap('china', geoJson as unknown as Parameters<typeof echarts.registerMap>[1]);
                setMapReady(true);
            })
            .catch(() => {
                setMapReady(true);
            });
    }, []);

    // ---- 3. 渲染地图 ----
    useEffect(() => {
        if (!mapReady || !stats || !mapChartRef.current) return;

        const regions = stats.topRegions;
        const maxVisits = Math.max(...regions.map((r) => r.visits), 1);

        const chart = echarts.init(mapChartRef.current);
        chart.setOption({
            tooltip: {
                trigger: 'item',
                formatter: (params: { name: string; value?: number }) =>
                    `${params.name}<br/>访问量：${params.value ? fmt(params.value) : '暂无数据'}`,
            },
            visualMap: {
                min: 0,
                max: maxVisits,
                left: 10,
                bottom: 10,
                text: ['高', '低'],
                textStyle: { color: '#aaa' },
                inRange: {
                    color: ['#1a0a3e', '#3b1a6e', '#6b2fa0', '#9b4dca', '#c084fc'],
                },
                calculable: true,
                itemWidth: 12,
                itemHeight: 100,
            },
            series: [
                {
                    type: 'map',
                    map: 'china',
                    roam: true,
                    selectedMode: false,
                    zoom: 1.5,
                    center: [104, 36],
                    label: { show: true, fontSize: 10, color: '#ccc' },
                    itemStyle: {
                        borderColor: 'rgba(255,255,255,0.15)',
                        areaColor: '#1a0a3e',
                    },
                    emphasis: {
                        label: { color: '#fff', fontSize: 12 },
                        itemStyle: { areaColor: '#8b5cf6' },
                    },
                    data: regions.map((r) => ({
                        name: r.location,
                        value: r.visits,
                    })),
                },
            ],
        });

        const handleResize = () => chart.resize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            chart.dispose();
        };
    }, [mapReady, stats]);

    // ---- 4. 页面访问柱状图 ----
    useEffect(() => {
        if (!stats || !barChartRef.current) return;

        const sorted = [...stats.topPages].sort((a, b) => b.visits - a.visits);
        const chart = echarts.init(barChartRef.current);
        chart.setOption({
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: Array<{ name: string; value: number }>) =>
                    `${params[0].name}<br/>访问量：${fmt(params[0].value)}<br/>独立访客：${fmt(
                        sorted.find((s) => shortPage(s.page_url) === params[0].name)?.unique_visitors ?? 0,
                    )}`,
            },
            grid: { left: '3%', right: '15%', bottom: '3%', top: '5%', containLabel: true },
            xAxis: { type: 'value', axisLabel: { color: '#999' }, splitLine: { lineStyle: { color: 'rgba(255,255,255,0.05)' } } },
            yAxis: {
                type: 'category',
                data: sorted.map((p) => shortPage(p.page_url)).reverse(),
                axisLabel: { color: '#ccc', fontSize: 12 },
                axisLine: { show: false },
                axisTick: { show: false },
            },
            series: [
                {
                    type: 'bar',
                    data: sorted.map((p) => p.visits).reverse(),
                    barWidth: 14,
                    itemStyle: {
                        borderRadius: [0, 6, 6, 0],
                        color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                            { offset: 0, color: '#6b2fa0' },
                            { offset: 1, color: '#c084fc' },
                        ]),
                    },
                },
            ],
        });

        const handleResize = () => chart.resize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            chart.dispose();
        };
    }, [stats]);

    // ---- 5. 地区占比饼图 ----
    useEffect(() => {
        if (!stats || !pieChartRef.current) return;

        const total = stats.topRegions.reduce((s, r) => s + r.visits, 0);
        const chart = echarts.init(pieChartRef.current);
        chart.setOption({
            tooltip: {
                trigger: 'item',
                formatter: (params: { name: string; value: number; percent: number }) =>
                    `${params.name}<br/>访问量：${fmt(params.value)}（${params.percent}%）`,
            },
            series: [
                {
                    type: 'pie',
                    radius: ['35%', '65%'],
                    center: ['50%', '55%'],
                    avoidLabelOverlap: true,
                    itemStyle: {
                        borderRadius: 4,
                        borderColor: 'rgba(15,15,25,0.8)',
                        borderWidth: 2,
                    },
                    label: {
                        show: true,
                        formatter: '{b}\n{d}%',
                        color: '#ccc',
                        fontSize: 11,
                    },
                    labelLine: { lineStyle: { color: 'rgba(255,255,255,0.2)' } },
                    emphasis: {
                        label: { fontSize: 14, fontWeight: 'bold' },
                        itemStyle: { shadowBlur: 10, shadowColor: 'rgba(139,92,246,0.4)' },
                    },
                    data: stats.topRegions
                        .sort((a, b) => b.visits - a.visits)
                        .slice(0, 8)
                        .map((r) => ({
                            name: r.location,
                            value: r.visits,
                        })),
                },
            ],
        });

        const handleResize = () => chart.resize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            chart.dispose();
        };
    }, [stats]);

    // ---- 6. 每日趋势折线图 ----
    useEffect(() => {
        if (!stats || !trendChartRef.current) return;

        const chart = echarts.init(trendChartRef.current);
        chart.setOption({
            tooltip: {
                trigger: 'axis',
                formatter: (params: Array<Record<string, unknown>>) => {
                    const day = params[0]?.name as string;
                    const visits = params.find((p) => (p as Record<string, unknown>).seriesName === '访问量');
                    const unique = params.find((p) => (p as Record<string, unknown>).seriesName === '独立访客');
                    return `${day}<br/>访问量：${visits ? fmt((visits as Record<string, number>).value) : '—'}<br/>独立访客：${unique ? fmt((unique as Record<string, number>).value) : '—'}`;
                },
            },
            legend: {
                data: ['访问量', '独立访客'],
                textStyle: { color: '#aaa' },
                top: 0,
            },
            grid: { left: '3%', right: '4%', bottom: '3%', top: '15%', containLabel: true },
            xAxis: {
                type: 'category',
                data: stats.dailyTrend.map((d) => d.day.slice(5)),
                axisLabel: { color: '#999', fontSize: 11 },
                splitLine: { show: false },
            },
            yAxis: {
                type: 'value',
                axisLabel: { color: '#999' },
                splitLine: { lineStyle: { color: 'rgba(255,255,255,0.05)' } },
            },
            series: [
                {
                    name: '访问量',
                    type: 'line',
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 6,
                    lineStyle: { width: 2, color: '#c084fc' },
                    itemStyle: { color: '#c084fc' },
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: 'rgba(139,92,246,0.35)' },
                            { offset: 1, color: 'rgba(139,92,246,0.02)' },
                        ]),
                    },
                    data: stats.dailyTrend.map((d) => d.visits),
                },
                {
                    name: '独立访客',
                    type: 'line',
                    smooth: true,
                    symbol: 'diamond',
                    symbolSize: 6,
                    lineStyle: { width: 2, color: '#f59e0b' },
                    itemStyle: { color: '#f59e0b' },
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: 'rgba(245,158,11,0.25)' },
                            { offset: 1, color: 'rgba(245,158,11,0.02)' },
                        ]),
                    },
                    data: stats.dailyTrend.map((d) => d.unique_visitors),
                },
            ],
        });

        const handleResize = () => chart.resize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            chart.dispose();
        };
    }, [stats]);

    // ---- 地区排名 ----
    const regionRanking = stats
        ? [...stats.topRegions].sort((a, b) => b.visits - a.visits)
        : [];

    // ---- 渲染 ----
    return (
        <div className="datav-container">
            {/* 顶部背景装饰 */}
            <div className="datav-header-bg" />

            {/* 标题栏 */}
            <header className="datav-header">
                <h1 className="datav-title">阿呆的博客 · 访客数据大屏</h1>
                <p className="datav-subtitle">Visitor Data Dashboard</p>
            </header>

            {/* 核心指标 */}
            <section className="datav-metrics">
                <div className="datav-metric-card">
                    <div className="datav-metric-icon total">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s2-6 11-6 11 6 11 6-2 6-11 6-11-6-11-6z" />
                            <circle cx="12" cy="12" r="3" />
                        </svg>
                    </div>
                    <div className="datav-metric-body">
                        <span className="datav-metric-label">总访问量</span>
                        <span className="datav-metric-value">{stats ? fmt(stats.totalVisits) : '—'}</span>
                    </div>
                </div>
                <div className="datav-metric-card">
                    <div className="datav-metric-icon unique">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                    </div>
                    <div className="datav-metric-body">
                        <span className="datav-metric-label">独立访客</span>
                        <span className="datav-metric-value">{stats ? fmt(stats.uniqueVisitors) : '—'}</span>
                    </div>
                </div>
                <div className="datav-metric-card">
                    <div className="datav-metric-icon today">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                            <polyline points="9 16 11 18 15 14" />
                        </svg>
                    </div>
                    <div className="datav-metric-body">
                        <span className="datav-metric-label">今日访问</span>
                        <span className="datav-metric-value">{stats ? fmt(stats.todayVisits) : '—'}</span>
                    </div>
                </div>
                <div className="datav-metric-card">
                    <div className="datav-metric-icon today-u">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                            <polyline points="17 6 23 6 23 12" />
                        </svg>
                    </div>
                    <div className="datav-metric-body">
                        <span className="datav-metric-label">今日独立访客</span>
                        <span className="datav-metric-value">{stats ? fmt(stats.todayUniqueVisitors) : '—'}</span>
                    </div>
                </div>
            </section>

            {/* 中栏：地图 + 地区排名 */}
            <section className="datav-middle">
                <div className="datav-panel datav-map-panel">
                    <div className="datav-panel-header">
                        <span className="datav-panel-dot" />
                        访客地域分布
                    </div>
                    <div className="datav-chart-wrapper">
                        <div ref={mapChartRef} className="datav-chart datav-map-chart" />
                        {!stats && <div className="datav-chart-empty">暂无数据</div>}
                    </div>
                </div>

                <div className="datav-panel datav-rank-panel">
                    <div className="datav-panel-header">
                        <span className="datav-panel-dot" />
                        地区访客排名
                    </div>
                    <div className="datav-rank-list">
                        {regionRanking.map((r, i) => (
                            <div key={r.location} className="datav-rank-item">
                                <span className={`datav-rank-num rank-${Math.min(i + 1, 3)}`}>
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                                <span className="datav-rank-name">{r.location}</span>
                                <div className="datav-rank-bar-bg">
                                    <div
                                        className="datav-rank-bar"
                                        style={{
                                            width: `${(r.visits / Math.max(regionRanking[0]?.visits, 1)) * 100}%`,
                                        }}
                                    />
                                </div>
                                <span className="datav-rank-value">{fmt(r.visits)}</span>
                            </div>
                        ))}
                        {regionRanking.length === 0 && (
                            <div className="datav-empty">暂无数据</div>
                        )}
                    </div>
                </div>
            </section>

            {/* 底栏：页面比较 + 占比 + 趋势 */}
            <section className="datav-bottom">
                <div className="datav-panel datav-bar-panel">
                    <div className="datav-panel-header">
                        <span className="datav-panel-dot" />
                        各页面访问比较
                    </div>
                    <div className="datav-chart-wrapper">
                        <div ref={barChartRef} className="datav-chart" />
                        {!stats && <div className="datav-chart-empty">暂无数据</div>}
                    </div>
                </div>

                <div className="datav-panel datav-pie-panel">
                    <div className="datav-panel-header">
                        <span className="datav-panel-dot" />
                        访客地区占比
                    </div>
                    <div className="datav-chart-wrapper">
                        <div ref={pieChartRef} className="datav-chart" />
                        {!stats && <div className="datav-chart-empty">暂无数据</div>}
                    </div>
                </div>

                <div className="datav-panel datav-trend-panel">
                    <div className="datav-panel-header">
                        <span className="datav-panel-dot" />
                        每日访问趋势
                    </div>
                    <div className="datav-chart-wrapper">
                        <div ref={trendChartRef} className="datav-chart" />
                        {!stats && <div className="datav-chart-empty">暂无数据</div>}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Visualize;
